import { useEffect, useState, useCallback, useRef } from 'react';
import {
  getBuscarAcessosWorkSpace,
  getBuscarAcessosDashboard,
  getBuscarEmbeddedToken
} from '../../api/dashboard';

const useFiltroPowerBIHook = ({
  selected,
  setSelected,
  setData,
  setLoading,
  setAlert,
  onViewReport,
  resetEmbed
}) => {
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const reportsCacheRef = useRef({});

  useEffect(() => {
    const getWorkspaces = async () => {
      setLoadingWorkspaces(true);
      try {
        const {
          data: { data: workspacesArr }
        } = await getBuscarAcessosWorkSpace();

        const workspaces = (workspacesArr || []).map(ws => ({
          id: String(ws.CODPOWERBIWORKSPACES),
          nome: ws.NOME_WORKSPACE
        }));

        setData({ workspaces, reports: [] });

        if (workspaces.length === 1) {
          const onlyWorkspace = workspaces[0];
          setSelected({ workspace: onlyWorkspace, report: null });
          await getDashboardsWorkspace(onlyWorkspace.id, true);
        }
      } catch (err) {
        const { mensagem, data: { tipoAlerta } = {} } = (err && err.response && err.response.data) || {};
        setAlert({
          message: mensagem || 'Falha ao carregar os workspaces.',
          type: tipoAlerta || 'error'
        });
      } finally {
        setLoadingWorkspaces(false);
      }
    };

    getWorkspaces();
  }, [setData, setSelected]);

  const getDashboardsWorkspace = useCallback(
    async (workspaceId, autoSelectSingle = false) => {
      if (!workspaceId) return [];

      const cached = reportsCacheRef.current[workspaceId];
      if (cached) {
        setData(prev => ({ ...prev, reports: cached }));
        if (autoSelectSingle && cached.length === 1) {
          setSelected(prev => ({ ...prev, report: cached[0] }));
        }
        return cached;
      }

      try {
        setLoading(true);
        const {
          data: { data: responseList }
        } = await getBuscarAcessosDashboard({ codWorkSpace: workspaceId });

        const reports = (responseList || []).map(r => ({
          id: String(r.CODPOWERBIRELATORIOS),
          nome: r.NOME_RELATORIO,
          dadosAtualizacao: r.DADOS_ATUALIZACAO || {}
        }));

        // Cache
        reportsCacheRef.current[workspaceId] = reports;

        setData(prev => ({ ...prev, reports }));

        if (autoSelectSingle && reports.length === 1) {
          setSelected(prev => ({ ...prev, report: reports[0] }));
        }

        return reports;
      } catch (err) {
        const { mensagem, data: { tipoAlerta } = {} } = (err && err.response && err.response.data) || {};
        setAlert({
          message: mensagem || 'Falha ao buscar relatórios do workspace.',
          type: tipoAlerta || 'error'
        });
        setData(prev => ({ ...prev, reports: [] }));
        return [];
      } finally {
        setLoading(false);
      }
    },
    [setAlert, setData, setLoading, setSelected]
  );

  const handleWorkspaceChange = useCallback(
    async (_, ws) => {
      setSelected({ workspace: ws, report: null });
      setData(prev => ({ ...prev, reports: [] }));
      if (ws) await getDashboardsWorkspace(ws.id, true);
    },
    [setSelected, setData, getDashboardsWorkspace]
  );

  const handleReportChange = useCallback(
    (_, rpt) => {
      setSelected(prev => ({ ...prev, report: rpt }));
    },
    [setSelected]
  );

  const handleViewReportClick = useCallback(async () => {
    if (!selected.workspace || !selected.report) return;
    setLoading(true);
    try {
      const {
        data: { data: payload }
      } = await getBuscarEmbeddedToken({
        codWorkSpace: selected.workspace.id,
        codRelatorio: selected.report.id
      });
      onViewReport(payload);
    } catch (err) {
      const { mensagem, data: { tipoAlerta } = {} } = (err && err.response && err.response.data) || {};
      setAlert({
        message: mensagem || 'Falha ao abrir o relatório.',
        type: tipoAlerta || 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [selected, setLoading, setAlert, onViewReport]);

  const refreshEmbeddedToken = useCallback(async () => {
    if (!selected.workspace || !selected.report) {
      setAlert({
        message: 'Não foi encontrado os dados de relatório e workspace. Favor contatar o suporte.',
        type: 'error'
      });
      resetEmbed();
    }
    const {
      data: { data: payload }
    } = await getBuscarEmbeddedToken({
      codWorkSpace: selected.workspace.id,
      codRelatorio: selected.report.id
    });
    return payload;
  }, [selected]);

  return {
    loadingWorkspaces,
    handleWorkspaceChange,
    handleReportChange,
    handleViewReportClick,
    refreshEmbeddedToken
  };
};

export default useFiltroPowerBIHook;
